/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, ReactElement, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Box, CircularProgress } from "@mui/material";
import moment from "moment";
import { Badge } from "@chakra-ui/react";
import axios from "axios";
import { AiOutlineFileSearch } from "react-icons/ai";
import { MdOutlineDoNotDisturbAlt } from "react-icons/md";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { Tooltip, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SweetAlert2 from "react-sweetalert2";
import UserUpdateStatusConfirmModal from "../../shared/UserUpdateStatusConfirmModal";
import AgGridPagination from "../../shared/AgGridPagination";

ModuleRegistry.registerModules([AllCommunityModule]);

type User = {
  user_id: string;
  username: string;
  email: string;
  role: string;
  status: string;
  merchant: any;
  agent: any;
  created_date: string;
  latest_login: string;
};

interface UserStatusItems {
  status: string;
  value: string;
  display: ReactElement;
}

interface Filters {
  [key: string]: any;
}

const initialFilters: Filters = {
  createdDateRange: [null, null],
};

const AgentUsersTab: React.FC<{
  currentUsername: string;
  agentId: string;
}> = ({ currentUsername, agentId }) => {
  const [isFetchingUsers, setIsFetchingUsers] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(10);
  const [agents, setAgents] = useState([]);
  const [swalProps, setSwalProps] = useState<any>({});
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const [isOpenUpdateStatusConfirmModal, setIsOpenUpdateStatusConfirmModal] =
    useState<boolean>(false);
  const [statusToUpdate, setStatusToUpdate] = useState<string>("");
  const [userDetailToUpdate, setUserDetailToUpdate] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (currentUsername) {
        fetchUsers();
      }

      const fetchedAgents = await getAgentOptions();
      setAgents(fetchedAgents);
    };

    fetchData();
  }, [page, pageSize, currentUsername]); // eslint-disable-line

  const fetchUsers = async () => {
    try {
      setIsFetchingUsers(true);
      const { createdDateRange } = initialFilters;

      const startDate = createdDateRange[0]?.toISOString() || "";
      const endDate = createdDateRange[1]?.toISOString() || "";

      const params = {
        agent_id: agentId,
        startDate,
        endDate,
        page: (page + 1).toString(),
        pageSize: pageSize.toString(),
        currentUsername,
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user/get-all-users`,
        { params }
      );
      let { data, totalCount } = response.data;

      setTableData(data);
      setRowCount(totalCount);

      setIsFetchingUsers(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setTableData([]);
      setIsFetchingUsers(false);
      setRowCount(0);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setPage(newValue.page);
    setPageSize(newValue.pageSize);
  };

  const getAgentOptions = async () => {
    localStorage.removeItem("agentName");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/datasource/agents`
      );
      if (response.data.agents && response.data.agents.payload) {
        return [
          { id: "", name: "-- Please Select --" },
          ...response.data.agents.payload,
        ];
      } else {
        console.error("Invalid response format for get-all-agents");
        return [{ id: "", name: "-- Please Select --" }];
      }
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const statusClasses = "status";

  const statusList: UserStatusItems[] = [
    {
      status: "Inactive",
      value: "inactive",
      display: (
        <Badge className={`${statusClasses} status-inactive`}>Inactive</Badge>
      ),
    },
    {
      status: "Active",
      value: "active",
      display: (
        <Badge className={`${statusClasses} status-active`}>Active</Badge>
      ),
    },
  ];

  const formatDate = (dateString: string) => {
    return moment(dateString, "YYYY-MM-DD HH:mm:ss").format(
      "DD MMM YY - hh:mm A"
    );
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  const EmailRenderer = (params: ICellRendererParams) => {
    const isCurrentUser = params.data.username === currentUsername;
    return (
      <span className={isCurrentUser ? "font-bold text-primary-500" : ""}>
        {params.data.email}
      </span>
    );
  };

  const UsernameRenderer = (params: ICellRendererParams) => {
    const isCurrentUser = params.data.username === currentUsername;
    return (
      <span className={isCurrentUser ? "font-bold text-primary-500" : ""}>
        {params.data.username}
      </span>
    );
  };

  const LatestLoginRenderer = (params: ICellRendererParams) => {
    const dateString = params.data.latest_login;
    if (!dateString) return <>-</>;
    return <>{formatDate(dateString)}</>;
  };

  const StatusRenderer = (params: ICellRendererParams) => {
    const status = params.data.status;
    const statusItem = statusList.find((item) => item.status === status);
    return <>{statusItem ? statusItem.display : null}</>;
  };

  const ActionsRenderer = (params: ICellRendererParams) => (
    <div className="flex items-center">
      <>
        <Tooltip title="View More Details" arrow placement="top">
          <IconButton
            disabled={isFetchingUsers}
            id={"button-detail-" + params.data?.user_id}
            onClick={() => handleViewDetail(params.data)}
            size="small"
          >
            <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={params.data.status === "Active" ? "Deactivate" : "Activate"}
          arrow
          placement="top"
        >
          <IconButton
            disabled={isFetchingUsers}
            id={"button-change-status-" + params.data?.user_id}
            onClick={() =>
              handleUpdateStatus(
                params.data.status === "Active" ? "inactive" : "active",
                params.data
              )
            }
            size="small"
          >
            {params.data.status === "Active" ? (
              <MdOutlineDoNotDisturbAlt className="h-5 w-5 text-red-500" />
            ) : (
              <AiOutlineCheckCircle className="h-5 w-5 text-primary-500" />
            )}
          </IconButton>
        </Tooltip>
      </>
    </div>
  );

  const columns: ColDef[] = [
    {
      field: "email",
      headerName: "Email",
      sortable: false,
      minWidth: 220,
      flex: 1,
      cellRenderer: EmailRenderer,
    },
    {
      field: "username",
      headerName: "Username",
      sortable: false,
      minWidth: 220,
      flex: 1,
      cellRenderer: UsernameRenderer,
    },
    {
      field: "latest_login",
      headerName: "Latest Login",
      sortable: false,
      minWidth: 220,
      flex: 1,
      cellRenderer: LatestLoginRenderer,
    },
    {
      field: "status",
      headerName: "",
      sortable: false,
      width: 140,
      cellRenderer: StatusRenderer,
      cellClass: "ag-cell-status",
    },
    {
      field: "actions",
      headerName: " ",
      sortable: false,
      width: 120,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const handleViewDetail = (userData: any) => {
    if (userData.agentId) {
      const agent = agents.filter((x: any) => x.id === userData.agentId)[0];
      if (agent) localStorage.setItem("agentName", agent.name);
    }
    navigate(`/${userRole}/user-management/${userData.user_id}`);
  };

  const handleUpdateStatus = (statusToUpdate: string, user: any) => {
    setUserDetailToUpdate(user);
    setStatusToUpdate(statusToUpdate);
    showUpdateStatusConfirmModal();
  };

  const showUpdateStatusConfirmModal = () => {
    setIsOpenUpdateStatusConfirmModal(true);
  };

  const closeUpdateStatusConfirmModal = () => {
    setIsOpenUpdateStatusConfirmModal(false);
  };

  const triggerReloadList = async () => {
    setUserDetailToUpdate(null);
    setStatusToUpdate("");
    setIsFetchingUsers(true);
    await fetchUsers();
  };

  const hidePagination = rowCount <= pageSize;

  return (
    <>
      {isOpenUpdateStatusConfirmModal && userDetailToUpdate && (
        <UserUpdateStatusConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenUpdateStatusConfirmModal}
          onClose={closeUpdateStatusConfirmModal}
          userToUpdate={userDetailToUpdate}
          statusToUpdate={statusToUpdate}
        />
      )}
      <SweetAlert2 {...swalProps} />
      <div className="px-4 py-8">
        <Box>
          <div style={{ width: "100%" }}>
            <AgGridReact
              theme={themeAlpine}
              rowData={isFetchingUsers ? [] : tableData}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              getRowId={(params) => params.data.email}
              rowHeight={48}
              headerHeight={44}
              domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
              suppressPaginationPanel={true}
              loading={isFetchingUsers}
              loadingOverlayComponent={CustomLoadingOverlay}
              overlayNoRowsTemplate="No data available"
            />
          </div>

          <AgGridPagination
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            onPaginationChange={setPaginationModel}
            hidden={hidePagination}
          />
        </Box>
      </div>
    </>
  );
};

export default AgentUsersTab;
